//  Las órdenes de compra (punto 7, migración 0044).
//
//  D78: **el avance de una orden no es un estado guardado, es una comparación**.
//  `v_ordenes_compra` y `v_orden_items` la hacen al mirar, así que acá no se
//  calcula nada. Esta pantalla sólo escribe decisiones humanas —crear, editar
//  una línea, cerrar, anular, reabrir—, todas por su función con permiso e
//  historial (D55 · D73), nunca por un `update` a la tabla.

import { abrirFicha, alAbrirOrden, alCambiarProveedores, cargarProveedoresFicha, veProveedores } from './proveedor';
import { abrirComoFicha } from './navegacion';
import { $, aviso, avisoHtml, dia, el, esc, fecha, limpiar, num, pedir, puede, rpc, sb, trabado, trabarSiNoContesto, pedirTodo, ordenarPor } from './nucleo';

// ---------------------------------------------------------------------
//  El estado del módulo
// ---------------------------------------------------------------------
//  Lo tecleado y no guardado vive ACÁ y no en el DOM (regla 6 del README): las
//  dos tablas se repintan enteras y vaciarían el `<input>`.

//  Las líneas de la orden que se está armando, antes de mandarla.
//  El costo es opcional y sólo lo escribe quien VE costos (D123).
let NUEVA: { producto_id: number; cantidad: number; costo: number | null }[] = [];
//  Las cantidades tecleadas en el detalle, por producto, sin guardar. Tienen
//  DOS dueños, por eso se guarda también `base` (lo que decía la línea al
//  empezar a teclear):
//
//  * la orden — un número escrito para la OC-700 no es de la OC-701; y
//  * el valor de esa línea — si cambió mientras tanto, lo tecleado se olvida;
//    si no, un valor viejo reaparece precargado y se guarda (la regla del 4/9,
//    regla 6 del README).
type Tecleado = { valor: string; base: number };
let EDICION: Record<number, Tecleado> = {};
//  El COSTO tecleado de cada línea, con la misma regla de los dos dueños;
//  `base` es el costo al empezar a escribir, como texto ('' si no tenía).
let COSTO_ED: Record<number, { valor: string; base: string }> = {};
//  La llegada estimada y las notas tecleadas (D230), con los mismos dos dueños:
//  la orden en la que se escribieron y lo que decía la orden al empezar.
type DatosTecleados = { orden: number; fecha: string; notas: string;
                        baseFecha: string; baseNotas: string };
let DATOS_ED: DatosTecleados | null = null;

//  EL COSTO EN LA ORDEN (0050). Verlo es `costo.ver`; ESCRIBIRLO es, además,
//  poder armar la orden. Con `compra.crear` sin verlo no se ofrece: escribiría
//  a ciegas y el historial no guarda cuál era (D123); además la base lo
//  rechaza con 42501 (0053, D128).
const veCosto = () => puede('costo','ver');
const escribeCosto = () => puede('compra','crear') && veCosto();
const textoCosto = (c: number | null | undefined) => c == null ? '' : String(Number(c));
//  Un costo leído: null es «sin costo», nunca un cero (D112).
const celdaCosto = (c: number | null | undefined) =>
  c == null ? '<span class="chip c-warn">sin costo</span>' : num(Number(c));
//  Lo que se escribió en un campo de costo: null si está vacío, NaN si no es
//  un número de cero para arriba.
const leerCosto = (txt: string) => {
  const t = txt.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
};
const colsItems = () => veCosto() ? 7 : 6;

let ORDENES: any[] = [];
let ITEMS: any[] = [];
let EVENTOS: any[] = [];
let PRODUCTOS: any[] = [];
let ORDEN_ABIERTA: number | null = null;
//  De qué orden es lo tecleado en EDICION: al volver a la lista la orden se
//  cierra, pero lo escrito sigue siendo de ella si se la vuelve a abrir.
let EDICION_DE: number | null = null;

//  Un error de lectura por consulta: cada tabla debe poder desdecirse sola (regla 5).
let ERROR_ORDENES = '';
let ERROR_ITEMS = '';
let ERROR_EVENTOS = '';
let ERROR_CATALOGO = '';
//  Los remitos de la orden abierta (D230). `null`: no se leyeron (sin permiso
//  o con error, que va en ERROR_REMITOS).
let REMITOS: any[] | null = null;
let ERROR_REMITOS = '';
//  La moneda del costo (`costo.moneda`), para el total. `null`: no se sabe.
let MONEDA_COSTO: string | null = null;

//  Cuántas órdenes se traen de una. Truncar en silencio sería mentir: cuando
//  llega justo el tope se dice.
const TOPE_ORDENES = 300;

const NADA = '—';

//  `fecha()` del núcleo trae la hora, que para un `date` es ruido y encima
//  puede correr un día por la zona horaria: para un `date`, `dia()`.

const CHIP_ESTADO: Record<string, string> = {
  abierta: 'c-pen', cerrada: 'c-ok', anulada: 'c-bad',
};
const CHIP_AVANCE: Record<string, string> = {
  sin_recibir: 'c-pen', parcial: 'c-warn', completa: 'c-ok', excedida: 'c-bad',
};
const TEXTO_AVANCE: Record<string, string> = {
  sin_recibir: 'sin recibir', parcial: 'parcial',
  completa: 'completa', excedida: 'llegó de más',
};

const chip = (valor: string | null, mapa: Record<string, string>,
              textos: Record<string, string> = {}) =>
  valor ? `<span class="chip ${mapa[valor] || 'c-pen'}">${esc(textos[valor] || valor)}</span>`
        : NADA;

// ---------------------------------------------------------------------
//  La carga
// ---------------------------------------------------------------------

export async function cargarCompras(){
  //  La tarjeta de armar una orden es sólo de quien puede armarlas. `toggle` y
  //  no `remove`: el permiso puede cambiar y debe poder volver a esconderse.
  $('#cardArmarOrden').classList.toggle('oculto', !puede('compra','crear'));
  //  Las columnas y los campos del costo, con la misma regla.
  $('#ocCostoLbl').classList.toggle('oculto', !escribeCosto());
  $('#thNuevaCosto').classList.toggle('oculto', !escribeCosto());
  $('#ordNuevoCostoLbl').classList.toggle('oculto', !escribeCosto());
  $('#thOrdCosto').classList.toggle('oculto', !veCosto());

  await Promise.all([cargarCatalogoCompras(), cargarOrdenes(), cargarProveedoresFicha(), leerMonedaCosto()]);
  //  El detalle se repinta solo si había una orden abierta: recargar la
  //  pestaña no tiene por qué cerrarla.
  if (ORDEN_ABIERTA != null) await cargarDetalle(ORDEN_ABIERTA);
}

alCambiarProveedores(() => { cargarCatalogoCompras(); });
//  Desde la ficha del proveedor, el número de una orden la abre acá (D230).
alAbrirOrden(id => abrirOrden(id));

//  Sin la fila en `config` la base usa 'USD' (como en la cuenta del proveedor).
async function leerMonedaCosto(){
  if (!veCosto()){ MONEDA_COSTO = null; return; }
  const { data, error } = await pedir(sb.from('config').select('valor').eq('clave', 'costo.moneda'));
  MONEDA_COSTO = error ? null : String((data || [])[0]?.valor ?? 'USD');
}

async function cargarCatalogoCompras(){
  ERROR_CATALOGO = '';
  if (!puede('compra','crear')) return;
  const [prov, prod] = await Promise.all([
    pedirTodo(() => sb.from('proveedores').select('id,nombre').eq('activo', true), { por: 'id', orden: ordenarPor('nombre', 'id') }),
    pedirTodo(() => sb.from('productos').select('id,codigo,descripcion,es_serializado')
      .eq('activo', true), { por: 'id', orden: ordenarPor('codigo', 'id') }),
  ]);

  //  Un catálogo que no se pudo leer NO es un catálogo vacío (regla 5): un
  //  `<select>` vacío también afirma.
  const falla = prov.error || prod.error;
  if (falla){
    ERROR_CATALOGO = falla.message;
    const roto = '<option value="">no se pudo leer el catálogo</option>';
    $('#ocProv').innerHTML = roto;
    $('#ocProd').innerHTML = roto;
    $('#ordNuevoProd').innerHTML = roto;
    PRODUCTOS = [];
    aviso('#ocMsg','bad',
      `No se pudo leer el catálogo (${falla.message}). No es que no haya ` +
      'proveedores ni productos: es que no se pudieron leer. Recargá antes de armar nada.');
    pintarNueva();
    return;
  }

  limpiar('#ocMsg');
  //  La opción vacía va SIEMPRE y va primera. Sin ella el `<select>` arranca con el
  //  primer proveedor elegido, el control «elegí el proveedor» nunca se dispara
  //  (`Number('2')` no es falsy) y la orden sale contra uno que nadie eligió.
  $('#ocProv').innerHTML = (prov.data || []).length
    ? '<option value="">— elegí el proveedor —</option>' +
      (prov.data || []).map((p: any) => `<option value="${p.id}">${esc(p.nombre)}</option>`).join('')
    : '<option value="">No hay proveedores cargados</option>';
  PRODUCTOS = prod.data || [];
  //  Lo mismo para el producto, por lo mismo (los tres selects de producto).
  const ops = PRODUCTOS.length
    ? '<option value="">— elegí el producto —</option>' +
      PRODUCTOS.map(p => `<option value="${p.id}">${esc(p.codigo)} — ${esc(p.descripcion)}</option>`).join('')
    : '<option value="">No hay productos cargados</option>';
  //  Mismo catálogo: armar una orden nueva y agregar una línea a una existente.
  $('#ocProd').innerHTML = ops;
  $('#ordNuevoProd').innerHTML = ops;
  pintarNueva();
}

export async function cargarOrdenes(){
  ERROR_ORDENES = '';
  //  El permiso se pregunta ANTES de consultar: sin `compra.ver` el RLS devuelve
  //  vacío con `error: null`, indistinguible de «no hay ninguna» (regla 5).
  if (!puede('compra','ver')){
    ORDENES = [];
    ERROR_ORDENES = 'No tenés permiso para ver las órdenes de compra.';
    pintarOrdenes();
    return;
  }
  const { data, error } = await pedir(sb.from('v_ordenes_compra').select('*')
    .order('orden_id', { ascending: false }).limit(TOPE_ORDENES));
  if (error){ ERROR_ORDENES = error.message; ORDENES = []; pintarOrdenes(); return; }
  ORDENES = data || [];
  pintarOrdenes();
}

// ---------------------------------------------------------------------
//  La lista
// ---------------------------------------------------------------------

function filtradas(){
  const q = ($('#buscaOrden').value || '').toLowerCase();
  const est = $('#filtroEstado').value;
  const av  = $('#filtroAvance').value;
  return ORDENES.filter(o =>
    (!est || o.estado === est) &&
    (!av  || o.avance === av) &&
    (!q || [o.numero, o.proveedor, o.notas].some(v => (v || '').toLowerCase().includes(q))));
}

function pintarOrdenes(){
  //  La tabla que afirma es la que se desdice: con la consulta rota no se dice
  //  «no hay ninguna orden».
  if (ERROR_ORDENES){
    $('#tblOrdenes').innerHTML =
      `<tr><td colspan="9" class="vacio">No se pudieron leer las órdenes: ${esc(ERROR_ORDENES)}</td></tr>`;
    $('#ordCuenta').textContent = '';
    return;
  }
  const f = filtradas();
  //  El número sale de una lista leída de verdad; si no, no se muestra.
  $('#ordCuenta').textContent = ORDENES.length
    ? `${f.length} de ${ORDENES.length} ${ORDENES.length === 1 ? 'orden' : 'órdenes'}` +
      (ORDENES.length === TOPE_ORDENES ? ' (las más nuevas; hay más)' : '')
    : '';
  $('#tblOrdenes').innerHTML = f.map(o => `
    <tr><td class="mono">${esc(o.numero)}</td>
        <td>${o.proveedor_id != null && o.proveedor && veProveedores()
          ? `<a href="#" data-fichaprov="${o.proveedor_id}">${esc(o.proveedor)}</a>` : esc(o.proveedor ?? NADA)}</td>
        <td>${dia(o.fecha_estimada)}</td>
        <td>${chip(o.estado, CHIP_ESTADO)}</td>
        <td>${chip(o.avance, CHIP_AVANCE, TEXTO_AVANCE)}</td>
        <td class="num">${num(o.pedido)}</td>
        <td class="num">${num(o.recibido)}</td>
        <td class="num">${o.pendiente ? `<b>${num(o.pendiente)}</b>` : NADA}</td>
        <td class="fila-acc">
          <button class="b b-sec verOrden" data-orden="${o.orden_id}">Ver</button></td></tr>
  `).join('') || `<tr><td colspan="9" class="vacio">${
    ORDENES.length ? 'Ninguna orden coincide con el filtro'
                   : 'Todavía no hay órdenes de compra'}</td></tr>`;
}

$('#buscaOrden').oninput = pintarOrdenes;
$('#filtroEstado').onchange = pintarOrdenes;
$('#filtroAvance').onchange = pintarOrdenes;

$('#tblOrdenes').onclick = e => {
  //  El nombre del proveedor abre su ficha (D223).
  const fp = el(e.target).closest('[data-fichaprov]');
  if (fp){ e.preventDefault(); return abrirFicha(Number(fp.dataset.fichaprov)); }
  const b = el(e.target).closest('.verOrden');
  if (!b) return;
  abrirOrden(Number(b.dataset.orden));
};

// ---------------------------------------------------------------------
//  El detalle
// ---------------------------------------------------------------------

async function abrirOrden(id: number){
  //  Lo tecleado tiene dueño: es de la orden en la que se escribió.
  if (EDICION_DE !== id) EDICION = {};
  EDICION_DE = id;
  ORDEN_ABIERTA = id;
  //  La orden ocupa la pestaña, con «← Volver a las órdenes» (D231). Abierta
  //  desde la ficha de su proveedor, vuelve a esa ficha.
  abrirComoFicha($('#cardOrden'), { lista: 'las órdenes', nombre: 'la orden',
    alCerrar: () => { ORDEN_ABIERTA = null; PEDIDO_DETALLE++; } });
  limpiar('#ordAccMsg'); limpiar('#ordLineaMsg'); limpiar('#ordDatosMsg'); limpiar('#ordMsg');
  await cargarDetalle(id);
}

//  Dos «Ver» seguidos son dos consultas en vuelo y gana la que contesta última:
//  este testigo evita que la respuesta lenta de la OC-700 pinte bajo la
//  cabecera de la OC-701 con los Guardar apuntando a la orden equivocada.
let PEDIDO_DETALLE = 0;

async function cargarDetalle(id: number){
  //  Un acto que termina con la orden ya cerrada (se volvió a la lista) no
  //  la relee.
  if (id == null) return false;
  const mio = ++PEDIDO_DETALLE;
  ERROR_ITEMS = ''; ERROR_EVENTOS = '';
  const [it, ev, rem] = await Promise.all([
    pedir(sb.from('v_orden_items').select('*').eq('orden_id', id).order('producto')),
    pedir(sb.from('v_orden_eventos').select('*').eq('orden_id', id).order('id', { ascending: false })),
    leerRemitos(id),
  ]);
  if (mio !== PEDIDO_DETALLE) return false;   // llegó tarde: ya hay otra orden abierta
  REMITOS = rem.filas; ERROR_REMITOS = rem.error;
  if (it.error){ ERROR_ITEMS = it.error.message; ITEMS = []; }
  else ITEMS = it.data || [];
  if (ev.error){ ERROR_EVENTOS = ev.error.message; EVENTOS = []; }
  else EVENTOS = ev.data || [];
  //  Se trae todo y recién después se pinta (regla 4): la cabecera muestra
  //  cosas de las dos consultas.
  pintarDetalle();
  return true;
}

//  Lo que entró contra esta orden (D230): las líneas de remito imputadas a
//  ella (`v_recepcion_items`) y, de cada remito, quién lo recibió
//  (`v_recepciones`). Las dos vistas son de `recepcion.ver`: sin ese permiso el
//  RLS contesta vacío sin error, así que se pregunta antes (regla 5).
async function leerRemitos(id: number): Promise<{ filas: any[] | null; error: string }>{
  if (!puede('recepcion','ver')) return { filas: null, error: '' };
  const li = await pedirTodo(() => sb.from('v_recepcion_items')
    .select('recepcion_item_id,recepcion_id,remito,ocurrido_en,producto,descripcion,cantidad')
    .eq('orden_id', id), { por: 'recepcion_item_id' });
  if (li.error) return { filas: null, error: li.error.message };
  const lineas = (li.data || []) as any[];
  const ids = [...new Set(lineas.map(l => Number(l.recepcion_id)))];
  let quien = new Map<number, string | null>();
  let errorQuien = '';
  if (ids.length){
    const r = await pedir(sb.from('v_recepciones').select('recepcion_id,recibio').in('recepcion_id', ids));
    if (r.error) errorQuien = r.error.message;
    else quien = new Map((r.data || []).map((x: any) => [Number(x.recepcion_id), x.recibio ?? null]));
  }
  //  Un renglón por remito, el más nuevo arriba.
  const porRemito = new Map<number, any>();
  for (const l of lineas){
    const k = Number(l.recepcion_id);
    if (!porRemito.has(k)) porRemito.set(k, { recepcion_id: k, remito: l.remito,
      ocurrido_en: l.ocurrido_en, recibio: errorQuien ? null : (quien.get(k) ?? null),
      sinQuien: !!errorQuien, lineas: [] as any[], unidades: 0 });
    const r = porRemito.get(k);
    r.lineas.push(l); r.unidades += Number(l.cantidad);
  }
  const filas = [...porRemito.values()].sort((a, b) =>
    String(b.ocurrido_en).localeCompare(String(a.ocurrido_en)) || b.recepcion_id - a.recepcion_id);
  return { filas, error: '' };
}

function ordenActual(){
  return ORDENES.find(o => Number(o.orden_id) === Number(ORDEN_ABIERTA)) || null;
}

function pintarDetalle(){
  const o = ordenActual();

  //  Las dos puertas —los botones y el formulario de líneas— se deciden ACÁ
  //  ARRIBA, antes de cualquier `return`, para TODAS las ramas. Ojo: no
  //  moverlas a `pintarItems`: con la ORDEN ilegible `pintarDetalle` vuelve
  //  antes y el formulario quedaría abierto con `ITEMS` vacío, sin el freno de
  //  «ese producto ya está en la orden», y «agregar» borraría lo pedido.
  const editable = !!o && puede('compra','crear')
                       && o.estado === 'abierta' && !ERROR_ITEMS;
  $('#ordAgregar').classList.toggle('oculto', !editable);
  $('#ordDatos').classList.toggle('oculto', !(o && puede('compra','crear') && o.estado === 'abierta'));

  if (!o){
    $('#ordTitulo').textContent = 'Orden';
    $('#ordCabecera').innerHTML =
      '<div class="aviso a-bad">No se pudo leer esta orden.</div>';
    //  «ESTA ORDEN», no «el detalle»: falló la cabecera, y las líneas pueden
    //  haberse leído bien.
    $('#tblOrdenItems').innerHTML =
      `<tr><td colspan="${colsItems()}" class="vacio">No se pudo leer esta orden</td></tr>`;
    $('#ordAcciones').innerHTML = '';
    avisoHtml('#ordAccMsg','bad', AVISO_SIN_ORDEN);
    $('#tblOrdenEventos').innerHTML =
      '<tr><td colspan="4" class="vacio">No se pudo leer el historial</td></tr>';
    limpiar('#ordTotal');
    pintarRemitos();
    return;
  }

  $('#ordTitulo').textContent = `Orden ${o.numero}`;
  $('#ordCabecera').innerHTML = `
    <div class="grid g4">
      <div><div class="hint">Proveedor</div><b>${o.proveedor_id != null && o.proveedor && veProveedores()
          ? `<a href="#" data-fichaprov="${o.proveedor_id}">${esc(o.proveedor)}</a>`
          : esc(o.proveedor ?? NADA)}</b></div>
      <div><div class="hint">Llegada estimada</div>${dia(o.fecha_estimada)}</div>
      <div><div class="hint">Estado</div>${chip(o.estado, CHIP_ESTADO)}</div>
      <div><div class="hint">Avance</div>${chip(o.avance, CHIP_AVANCE, TEXTO_AVANCE)}</div>
      <div><div class="hint">La armó</div>${esc(o.creo ?? NADA)}</div>
      <div><div class="hint">Cuándo</div>${fecha(o.creado_en)}</div>
      ${o.cerrada_en ? `
      <div><div class="hint">La cerró</div>${esc(o.cerro ?? NADA)}</div>
      <div><div class="hint">Cuándo</div>${fecha(o.cerrada_en)}</div>` : ''}
    </div>
    ${o.notas && $('#ordDatos').classList.contains('oculto')
      ? `<p class="hint" style="margin-top:0.471rem">${esc(o.notas)}</p>` : ''}
    ${o.motivo_cierre ? `<div class="aviso a-info" style="margin-top:0.471rem">
        <b>Motivo:</b> ${esc(o.motivo_cierre)}</div>` : ''}`;

  pintarDatos(o);
  pintarItems(o, editable);
  pintarTotal();
  pintarAcciones(o);
  pintarRemitos();
  pintarEventos();
}

//  El nombre del proveedor de la cabecera abre su ficha (D230).
$('#ordCabecera').onclick = e => {
  const fp = el(e.target).closest('[data-fichaprov]');
  if (fp){ e.preventDefault(); abrirFicha(Number(fp.dataset.fichaprov)); }
};

// ---------------------------------------------------------------------
//  La llegada estimada y las notas (D230, `editar_orden_datos`)
// ---------------------------------------------------------------------
function pintarDatos(o: any){
  if ($('#ordDatos').classList.contains('oculto')) return;
  const fecha = o.fecha_estimada ? String(o.fecha_estimada).slice(0, 10) : '';
  const notas = o.notas ?? '';
  //  Lo tecleado vale si es de esta orden y la orden sigue diciendo lo mismo
  //  que al empezar a escribir; si no, se olvida.
  if (!DATOS_ED || DATOS_ED.orden !== Number(o.orden_id)
      || DATOS_ED.baseFecha !== fecha || DATOS_ED.baseNotas !== notas)
    DATOS_ED = { orden: Number(o.orden_id), fecha, notas, baseFecha: fecha, baseNotas: notas };
  $('#ordFecha').value = DATOS_ED.fecha;
  $('#ordNotas').value = DATOS_ED.notas;
}
$('#ordFecha').oninput = () => { if (DATOS_ED) DATOS_ED.fecha = $('#ordFecha').value; };
$('#ordNotas').oninput = () => { if (DATOS_ED) DATOS_ED.notas = $('#ordNotas').value; };

$('#btnOrdDatos').onclick = async () => {
  limpiar('#ordDatosMsg');
  const o = ordenActual();
  if (!o || !DATOS_ED || DATOS_ED.orden !== Number(o.orden_id)) return;
  //  Va sólo lo que cambió: la base cambia sólo lo que trae.
  const datos: Record<string, string> = {};
  if (DATOS_ED.fecha !== DATOS_ED.baseFecha) datos.fecha_estimada = DATOS_ED.fecha;
  if (DATOS_ED.notas.trim() !== DATOS_ED.baseNotas.trim()) datos.notas = DATOS_ED.notas;
  if (!Object.keys(datos).length){ aviso('#ordDatosMsg', 'warn', 'No cambiaste nada.'); return; }
  const btn = $('#btnOrdDatos');
  btn.disabled = true;
  try {
    const { data, error } = await rpc('editar_orden_datos',
      { p_orden_id: Number(o.orden_id), p_datos: datos });
    if (error){ aviso('#ordDatosMsg', 'bad', error.message); return; }
    DATOS_ED = null;
    await cargarOrdenes();
    if (!await cargarDetalle(Number(o.orden_id))) return;
    aviso('#ordDatosMsg', 'ok', (data as any)?.mensaje || 'Listo.');
  } finally { btn.disabled = false; }
};

// ---------------------------------------------------------------------
//  El total en plata (D230): sólo para quien ve costos
// ---------------------------------------------------------------------
function pintarTotal(){
  if (!veCosto() || ERROR_ITEMS || !ITEMS.length){ limpiar('#ordTotal'); return; }
  const conCosto = ITEMS.filter(i => i.costo != null);
  const sinCosto = ITEMS.length - conCosto.length;
  if (!conCosto.length){
    $('#ordTotal').innerHTML = '<p class="hint">Ninguna línea tiene costo: la orden no tiene total.</p>';
    return;
  }
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const total = r2(conCosto.reduce((a, i) => a + Number(i.pedido) * Number(i.costo), 0));
  const falta = r2(conCosto.reduce((a, i) => a + Number(i.pendiente || 0) * Number(i.costo), 0));
  const mon = MONEDA_COSTO ? ` ${esc(MONEDA_COSTO)}` : '';
  $('#ordTotal').innerHTML = `<p style="margin-top:0.471rem">Total de la orden: <b>${num(total)}${mon}</b>
    · Falta llegar: <b>${num(falta)}${mon}</b>${sinCosto
      ? ` <span class="hint">(${sinCosto === 1 ? 'una línea sin costo no suma' : `${sinCosto} líneas sin costo no suman`})</span>`
      : ''}</p>`;
}

// ---------------------------------------------------------------------
//  Los remitos de la orden (D230)
// ---------------------------------------------------------------------
function pintarRemitos(){
  if (!puede('recepcion','ver')){
    $('#ordRemitos').innerHTML = '<p class="hint">No tenés permiso para ver los remitos.</p>'; return;
  }
  if (ERROR_REMITOS){
    $('#ordRemitos').innerHTML = `<p class="vacio">No se pudieron leer los remitos de esta orden (${esc(ERROR_REMITOS)}). No quiere decir que no tenga.</p>`;
    return;
  }
  const rs = REMITOS || [];
  $('#ordRemitos').innerHTML = rs.length
    ? `<div class="tw"><table><thead><tr><th>Cuándo</th><th>Remito</th><th>Recibió</th>
         <th>Qué trajo</th><th class="num">Unidades</th></tr></thead><tbody>${
        rs.map(r => `<tr><td>${esc(fecha(r.ocurrido_en))}</td><td class="mono">${esc(r.remito || NADA)}</td>
          <td>${r.sinQuien ? '<span class="hint">no se pudo leer</span>' : esc(r.recibio ?? NADA)}</td>
          <td>${r.lineas.map((l: any) => `${esc(l.producto)} × ${num(Number(l.cantidad))}`).join(', ')}</td>
          <td class="num">${num(r.unidades)}</td></tr>`).join('')
      }</tbody></table></div>`
    : '<p class="vacio">Todavía no entró nada contra esta orden.</p>';
}

function pintarItems(o: any, editable: boolean){
  //  Lo tecleado de una línea que ya no está en la orden se olvida (si no,
  //  reaparece precargado al volver a agregar ese producto). Sólo se poda si las
  //  líneas se LEYERON: un error de lectura no es un cambio.
  if (!ERROR_ITEMS)
    [EDICION, COSTO_ED].forEach(ed => Object.keys(ed).forEach(k => {
      if (!ITEMS.some(i => Number(i.producto_id) === Number(k))) delete ed[Number(k)];
    }));

  //  Otra vez la tabla que afirma: sin leer el detalle no se dice «no tiene
  //  líneas» (una orden sin líneas no existe) ni se muestra ningún número.
  if (ERROR_ITEMS){
    $('#tblOrdenItems').innerHTML =
      `<tr><td colspan="${colsItems()}" class="vacio">No se pudo leer el detalle de la orden: ${esc(ERROR_ITEMS)}</td></tr>`;
    return;
  }
  $('#tblOrdenItems').innerHTML = ITEMS.map(i => `
    <tr><td>${esc(i.producto)} — ${esc(i.descripcion)}</td>
        <td class="num">${editable
          ? `<input class="ocLinCant" type="number" min="0" step="1" style="width:5.294rem"
                    data-prod="${i.producto_id}" data-base="${i.pedido}"
                    value="${esc(valorTecleado(i))}">`
          : num(i.pedido)}</td>
        ${!veCosto() ? '' : `<td class="num">${editable && escribeCosto()
          ? `<input class="ocLinCosto" type="number" min="0" step="0.01" style="width:5.882rem"
                    data-prod="${i.producto_id}" data-base="${esc(textoCosto(i.costo))}"
                    placeholder="sin costo" value="${esc(valorCosto(i))}">`
          : celdaCosto(i.costo)}</td>`}
        <td class="num">${num(i.recibido)}</td>
        <td class="num">${i.pendiente ? `<b>${num(i.pendiente)}</b>` : NADA}</td>
        <td class="num">${i.excedente
          ? `<span class="chip c-bad">${num(i.excedente)}</span>` : NADA}</td>
        <td class="fila-acc">${editable
          ? `<button class="b b-sec guardarLinea" data-prod="${i.producto_id}">Guardar</button>`
          : ''}</td></tr>
  `).join('') || `<tr><td colspan="${colsItems()}" class="vacio">Esta orden no tiene líneas</td></tr>`;
}

function valorCosto(i: any){
  const ed = COSTO_ED[i.producto_id];
  if (ed && ed.base === textoCosto(i.costo)) return ed.valor;
  delete COSTO_ED[i.producto_id];
  return textoCosto(i.costo);
}

//  Lo tecleado vale mientras la línea siga diciendo lo mismo que al empezar a
//  escribir; si cambió, se olvida (un número viejo se guardaría como actual).
function valorTecleado(i: any){
  const ed = EDICION[i.producto_id];
  if (ed && ed.base === Number(i.pedido)) return ed.valor;
  delete EDICION[i.producto_id];
  return String(i.pedido);
}

function pintarAcciones(o: any){
  const acc: string[] = [];
  if (puede('compra','crear')){
    if (o.estado === 'abierta'){
      //  Cerrar y anular se esconden mientras no se sepa qué llegó: cerrar sin
      //  ver el detalle es dar por terminada una lista que puede no ser la real,
      //  y anular es decir «nunca existió» sin saber si entró mercadería (regla 5).
      if (!ERROR_ITEMS){
        acc.push('<button class="b b-sec" id="btnCerrarOrden">Cerrar la orden</button>');
        acc.push('<button class="b b-peligro" id="btnAnularOrden">Anular</button>');
      }
    } else {
      acc.push('<button class="b b-sec" id="btnReabrirOrden">Reabrir</button>');
    }
  }
  $('#ordAcciones').innerHTML = acc.join('');
  //  El `else` es necesario: un aviso que sólo sabe aparecer se queda pegado y
  //  miente cuando la lectura se recupera.
  if (sinDetalle(o)) avisoHtml('#ordAccMsg', 'bad', AVISO_SIN_DETALLE);
  else limpiar('#ordAccMsg');

  const btnCerrar  = document.getElementById('btnCerrarOrden');
  const btnAnular  = document.getElementById('btnAnularOrden');
  const btnReabrir = document.getElementById('btnReabrirOrden');
  if (btnCerrar)  btnCerrar.onclick  = () => pedirMotivo('cerrar', o);
  if (btnAnular)  btnAnular.onclick  = () => pedirMotivo('anular', o);
  if (btnReabrir) btnReabrir.onclick = () => pedirMotivo('reabrir', o);
}

//  El aviso vive en una constante porque lo escriben DOS lugares: el pintado y
//  el final de cerrar/anular/reabrir. Si el «listo» lo pisara, la advertencia
//  de que el detalle no se leyó desaparecería justo cuando más se necesita.
const AVISO_SIN_ORDEN =
  'No se pudo leer esta orden, así que no se puede saber en qué estado está. ' +
  '<b>No la cierres ni le toques una línea hasta saberlo.</b>';

const AVISO_SIN_DETALLE =
  'No se pudo leer qué llegó contra esta orden, así que cerrarla, anularla o ' +
  'tocarle una línea queda escondido. <b>No la cierres hasta saberlo.</b>';

const sinDetalle = (o: any) =>
  !!ERROR_ITEMS && puede('compra','crear') && o.estado === 'abierta';

function pintarEventos(){
  if (ERROR_EVENTOS){
    $('#tblOrdenEventos').innerHTML =
      `<tr><td colspan="4" class="vacio">No se pudo leer el historial: ${esc(ERROR_EVENTOS)}</td></tr>`;
    return;
  }
  $('#tblOrdenEventos').innerHTML = EVENTOS.map(v => `
    <tr><td>${fecha(v.ocurrido_en)}</td>
        <td>${esc(String(v.tipo || '').replace(/_/g, ' '))}${detalleEvento(v)}</td>
        <td>${esc(v.quien ?? NADA)}</td>
        <td>${esc(v.motivo ?? NADA)}</td></tr>
  `).join('') || '<tr><td colspan="4" class="vacio">Sin movimientos</td></tr>';
}

//  El detalle es un jsonb libre: se muestran las claves que la 0044 escribe
//  de verdad y el resto se ignora, para que agregar una no rompa la tabla.
function detalleEvento(v: any){
  const d = v.detalle || {};
  const partes: string[] = [];
  if (d.lineas != null) partes.push(`${d.lineas} línea${d.lineas === 1 ? '' : 's'}`);
  if (d.antes != null && d.ahora != null) partes.push(`de ${d.antes} a ${d.ahora}`);
  else if (d.ahora != null) partes.push(`cantidad ${d.ahora}`);
  if (d.estaba) partes.push(`estaba ${d.estaba}`);
  //  D230: la llegada estimada, con lo que decía y lo que dice; las notas, sólo
  //  que cambiaron.
  if ('fecha_ahora' in d)
    partes.push(`llegada de ${d.fecha_antes ? dia(d.fecha_antes) : 'sin fecha'} a ${d.fecha_ahora ? dia(d.fecha_ahora) : 'sin fecha'}`);
  if (d.notas_cambiadas === true) partes.push('cambió las notas');
  //  El costo NO viaja en el historial (D114): sólo que cambió.
  if (v.tipo === 'costo_editado') partes.length = 0;
  //  «cambió el costo» también si cambió junto con la cantidad: «con costo» se
  //  leería como «la línea tiene costo».
  if (d.costo_cambiado === true)
    partes.push(v.tipo === 'linea_agregada' ? 'con costo' : 'cambió el costo');
  return partes.length ? ` <span class="hint">(${esc(partes.join(', '))})</span>` : '';
}

// ---------------------------------------------------------------------
//  Editar una línea
// ---------------------------------------------------------------------
//  Lo tecleado se guarda en EDICION en cada tecla: sin eso, guardar una
//  fila repinta la tabla y borra los números escritos en las otras.

$('#tblOrdenItems').oninput = e => {
  const c = el(e.target).closest('.ocLinCosto');
  if (c){ COSTO_ED[Number(c.dataset.prod)] = { valor: c.value, base: c.dataset.base }; return; }
  const i = el(e.target).closest('.ocLinCant');
  if (!i) return;
  EDICION[Number(i.dataset.prod)] = { valor: i.value, base: Number(i.dataset.base) };
};

$('#tblOrdenItems').onclick = e => {
  const b = el(e.target).closest('.guardarLinea');
  if (!b) return;
  const prod = Number(b.dataset.prod);
  const linea = ITEMS.find(i => Number(i.producto_id) === prod);
  //  Sin nada tecleado en la cantidad, vale la que la línea ya tiene: quien
  //  sólo cambió el costo no tiene por qué reescribirla.
  const txt = (EDICION[prod]?.valor ?? String(linea?.pedido ?? '')).trim();
  if (txt === ''){
    aviso('#ordLineaMsg','bad','Escribí la cantidad. Poné 0 si lo que querés es sacar la línea.');
    return;
  }
  const cant = Number(txt);
  if (!Number.isInteger(cant) || cant < 0){
    aviso('#ordLineaMsg','bad','La cantidad tiene que ser un número entero de cero para arriba.');
    return;
  }
  //  El costo, sólo si se ofreció y se tocó. Vacío es «no dije nada» (la base
  //  no borra el que está): vaciar uno existente se rechaza en vez de fingir.
  let costo: number | null = null;
  const ce = COSTO_ED[prod];
  if (escribeCosto() && ce && cant > 0){
    costo = leerCosto(ce.valor);
    if (Number.isNaN(costo)){
      aviso('#ordLineaMsg','bad','El costo tiene que ser un número de cero para arriba.'); return;
    }
    if (costo === null && ce.base !== ''){
      aviso('#ordLineaMsg','bad','Un costo acordado no se puede borrar, sólo cambiar. ' +
        'Si no corresponde, poné el que sí corresponde.');
      return;
    }
  }
  guardarLinea(prod, cant, b, costo);
};

async function guardarLinea(producto_id: number, cantidad: number, btn: any,
                            costo: number | null = null){
  limpiar('#ordLineaMsg');
  const antes = ITEMS.find(i => Number(i.producto_id) === producto_id)?.costo;
  const costoAntes = antes == null ? null : Number(antes);
  btn.disabled = true;
  try {
    //  `p_costo` va SÓLO cuando hay uno: sin la clave, la base conserva el
    //  que está (`null` es «no dije nada», no «ninguno»).
    const { data, error } = await rpc('editar_orden_linea', {
      p_orden_id: ORDEN_ABIERTA as number, p_producto_id: producto_id, p_cantidad: cantidad,
      ...(costo == null ? {} : { p_costo: costo }) });
    if (error){ aviso('#ordLineaMsg','bad', error.message); return; }
    //  Recién acá se olvida lo tecleado de ESA línea: ya está guardado.
    delete EDICION[producto_id];
    delete COSTO_ED[producto_id];
    //  Primero se recarga, después se avisa (si no, el repintado lo pisa).
    await cargarOrdenes();
    //  Si mientras tanto se abrió otra orden, no se avisa nada.
    if (!await cargarDetalle(ORDEN_ABIERTA as number)) return;
    const q: Record<string, string> = {
      linea_agregada: 'Línea agregada.', linea_editada: 'Cantidad cambiada.',
      linea_quitada: 'Línea quitada.', sin_cambios: 'Ya decía eso: no se cambió nada.',
      costo_editado: 'Costo cambiado.',
    };
    //  Y si cambiaron las dos cosas, se dicen las dos: la base contesta
    //  `linea_editada`, que sola dice «Cantidad cambiada».
    const tambienCosto = data?.accion === 'linea_editada' && costo != null && costo !== costoAntes;
    aviso('#ordLineaMsg','ok', tambienCosto ? 'Cantidad y costo cambiados.'
                                            : q[data?.accion as string] || 'Listo.');
    //  El costo de la línea nueva se borra recién ahora, con la línea guardada.
    if (btn === $('#btnOrdAgregar')) $('#ordNuevoCosto').value = '';
  } finally { btn.disabled = false; }
}

//  Agregar una línea nueva a la orden abierta.
$('#btnOrdAgregar').onclick = () => {
  limpiar('#ordLineaMsg');
  const prod = Number($('#ordNuevoProd').value);
  const cant = Number($('#ordNuevaCant').value || 0);
  if (!prod){ aviso('#ordLineaMsg','bad','Elegí el producto.'); return; }
  if (!Number.isInteger(cant) || cant <= 0){
    aviso('#ordLineaMsg','bad','La cantidad tiene que ser mayor a cero.'); return;
  }
  //  `editar_orden_linea` FIJA la cantidad, no la suma: con el producto ya en la
  //  orden, «Agregar» borraría lo pedido sin decirlo (10 más 3 daba 3). En la
  //  tarjeta de armar una orden el mismo gesto SÍ suma; para no tener dos gestos
  //  iguales con resultados opuestos, acá se frena y manda a la fila.
  const ya = ITEMS.find(i => Number(i.producto_id) === prod);
  if (ya){
    aviso('#ordLineaMsg','warn',
      `${ya.producto} ya está en esta orden, con ${ya.pedido} pedidas. ` +
      'Cambiá la cantidad en su fila y guardá: el número que escribas ahí es el que queda.');
    return;
  }
  const costo = escribeCosto() ? leerCosto($('#ordNuevoCosto').value || '') : null;
  if (Number.isNaN(costo)){
    aviso('#ordLineaMsg','bad','El costo tiene que ser un número de cero para arriba.'); return;
  }
  $('#ordNuevaCant').value = '1';
  guardarLinea(prod, cant, $('#btnOrdAgregar'), costo);
};

// ---------------------------------------------------------------------
//  Cerrar, anular, reabrir
// ---------------------------------------------------------------------
//  Los tres piden el motivo ANTES de hacer nada (obligatorio en la base). El
//  cuadro lleva su propio hueco de avisos (#ordConfMsg) para que el error de
//  «falta el motivo» no borre el cuadro.

const ACTOS: Record<string, {
  titulo: (o: any) => string; texto: (o: any) => string;
  boton: string; clase: string; funcion: 'cerrar_orden' | 'anular_orden' | 'reabrir_orden';
}> = {
  cerrar: {
    titulo: o => `Cerrar la orden ${o.numero}`,
    texto: o => o.pendiente
      ? `Quedan <b>${num(o.pendiente)}</b> unidades sin recibir. Cerrarla es decir que
         no vienen más: la orden deja de aparecer para imputar remitos.`
      : 'La orden deja de aparecer para imputar remitos.',
    boton: 'Cerrar la orden', clase: 'b-pri', funcion: 'cerrar_orden',
  },
  anular: {
    titulo: o => `Anular la orden ${o.numero}`,
    texto: () => `Anular es decir que esta orden nunca existió. Sólo se puede si no
                  entró <b>nada</b> contra ella; si entró algo, lo que corresponde es cerrarla.`,
    boton: 'Anular', clase: 'b-peligro', funcion: 'anular_orden',
  },
  reabrir: {
    titulo: o => `Reabrir la orden ${o.numero}`,
    texto: () => `Vuelve a estar abierta y se le pueden volver a imputar remitos.
                  El motivo del cierre se borra de la orden, pero queda en el historial.`,
    boton: 'Reabrir', clase: 'b-pri', funcion: 'reabrir_orden',
  },
};

function pedirMotivo(acto: keyof typeof ACTOS | string, o: any){
  const a = ACTOS[acto];
  limpiar('#ordAccMsg');
  $('#ordAccMsg').innerHTML = `
    <div class="confirmar${acto === 'anular' ? ' grave' : ''}">
      <div class="conf-tit">${esc(a.titulo(o))}</div>
      <p class="conf-txt">${a.texto(o)}</p>
      <label>Por qué <input id="ordMotivo" placeholder="El proveedor no tiene más stock"></label>
      <div class="fila-acc" style="margin-top:0.471rem">
        <button class="b b-sec" id="ordConfNo">Dejarla como está</button>
        <button class="b ${a.clase}" id="ordConfOk">${esc(a.boton)}</button>
      </div>
      <div id="ordConfMsg"></div>
    </div>`;
  $('#ordConfNo').onclick = () => limpiar('#ordAccMsg');
  $('#ordConfOk').onclick = async () => {
    const motivo = ($('#ordMotivo').value || '').trim();
    if (!motivo){
      aviso('#ordConfMsg','bad','Escribí por qué: es lo que va a leer el que mañana se pregunte qué pasó.');
      $('#ordMotivo').focus();
      return;
    }
    const btn = $('#ordConfOk'); btn.disabled = true;
    try {
      const { error } = await rpc(a.funcion, {
        p_orden_id: ORDEN_ABIERTA as number, p_motivo: motivo });
      //  El error va ADENTRO del cuadro: el motivo escrito no se pierde.
      if (error){ aviso('#ordConfMsg','bad', error.message); return; }
      limpiar('#ordAccMsg');
      await cargarOrdenes();
      if (!await cargarDetalle(ORDEN_ABIERTA as number)) return;
      const nueva = ordenActual() || o;
      const listo = `Orden ${o.numero}: listo.`;
      if (sinDetalle(nueva))
        avisoHtml('#ordAccMsg','warn', esc(listo) + '<br><br>' + AVISO_SIN_DETALLE);
      else aviso('#ordAccMsg','ok', listo);
    } finally { btn.disabled = false; }
  };
}

// ---------------------------------------------------------------------
//  Armar una orden nueva
// ---------------------------------------------------------------------

function pintarNueva(){
  $('#tblNuevaOrden').innerHTML = NUEVA.map((l, i) => {
    const p = PRODUCTOS.find(x => Number(x.id) === l.producto_id);
    return `<tr><td>${p ? `${esc(p.codigo)} — ${esc(p.descripcion)}` : `#${l.producto_id}`}</td>
      <td class="num">${num(l.cantidad)}</td>
      ${escribeCosto() ? `<td class="num">${celdaCosto(l.costo)}</td>` : ''}
      <td class="fila-acc"><button class="b b-sec quitarLinea" data-i="${i}">Quitar</button></td></tr>`;
  }).join('') || `<tr><td colspan="${escribeCosto() ? 4 : 3}" class="vacio">Todavía no agregaste ninguna línea</td></tr>`;
  $('#btnOcCrear').disabled = NUEVA.length === 0 || !!ERROR_CATALOGO || trabado('#btnOcCrear');
  $('#btnOcAgregar').disabled = !!ERROR_CATALOGO;
  $('#btnOrdAgregar').disabled = !!ERROR_CATALOGO;
}

$('#btnOcAgregar').onclick = () => {
  limpiar('#ocMsg');
  const prod = Number($('#ocProd').value);
  const cant = Number($('#ocCant').value || 0);
  if (!prod){ aviso('#ocMsg','bad','Elegí el producto.'); return; }
  if (!Number.isInteger(cant) || cant <= 0){
    aviso('#ocMsg','bad','La cantidad tiene que ser mayor a cero.'); return;
  }
  const costo = escribeCosto() ? leerCosto($('#ocCosto').value || '') : null;
  if (Number.isNaN(costo)){
    aviso('#ocMsg','bad','El costo tiene que ser un número de cero para arriba.'); return;
  }
  //  Dos veces el mismo producto se suma en una línea, como hace la base. El
  //  costo, también como la base: el nuevo manda si vino; si no, queda el que
  //  estaba (no se suma).
  const ya = NUEVA.find(l => l.producto_id === prod);
  if (ya){ ya.cantidad += cant; if (costo != null) ya.costo = costo; }
  else NUEVA.push({ producto_id: prod, cantidad: cant, costo });
  $('#ocCant').value = '1';
  $('#ocCosto').value = '';
  pintarNueva();
};

$('#tblNuevaOrden').onclick = e => {
  const b = el(e.target).closest('.quitarLinea');
  if (!b) return;
  NUEVA.splice(Number(b.dataset.i), 1);
  pintarNueva();
};

$('#btnOcCrear').onclick = async () => {
  limpiar('#ocMsg');
  if (!NUEVA.length){ aviso('#ocMsg','bad','Una orden sin líneas no es una orden.'); return; }
  const prov = Number($('#ocProv').value);
  if (!prov){ aviso('#ocMsg','bad','Elegí el proveedor.'); return; }
  const btn = $('#btnOcCrear'); btn.disabled = true;
  try {
    const { data, error } = await rpc('crear_orden_compra', {
      p_proveedor_id  : prov,
      //  La clave `costo` va sólo en las líneas que tienen uno.
      p_lineas        : NUEVA.map(l => l.costo == null
                          ? { producto_id: l.producto_id, cantidad: l.cantidad }
                          : l) as any,
      p_fecha_estimada: $('#ocFecha').value || null,
      p_notas         : $('#ocNotas').value.trim() || null,
    });
    //  Una orden que no contestó quizá quedó creada: el botón no vuelve hasta
    //  que se entre de nuevo a la pestaña, que relee las órdenes (D91, D92).
    trabarSiNoContesto('#btnOcCrear', error);
    if (error){ aviso('#ocMsg','bad', error.message); return; }
    //  Recién con la orden creada se olvida lo tecleado.
    NUEVA = [];
    $('#ocFecha').value = ''; $('#ocNotas').value = ''; $('#ocCant').value = '1';
    pintarNueva();
    await cargarOrdenes();
    const creada = `Orden <b>${esc(data?.numero ?? '')}</b> creada con ${num(data?.lineas)} ` +
      `línea${data?.lineas === 1 ? '' : 's'}.`;
    avisoHtml('#ocMsg','ok', creada);
    //  La orden nueva se abre como ficha (D231): el cartel va también ahí,
    //  que es lo que se ve.
    if (data?.orden_id){
      await abrirOrden(Number(data.orden_id));
      avisoHtml('#ordMsg','ok', creada);
    }
  } finally { btn.disabled = NUEVA.length === 0 || !!ERROR_CATALOGO || trabado('#btnOcCrear'); }
};
